import { Box } from '@chakra-ui/react';
import {
  useGetAnalyticsCountByPriorityExport,
  useGetAnalyticsCountByStatus,
  useGetAnalyticsCountByStatusExport,
} from 'views/main/organization/analytics-v3/hooks/useGetAnalyticsData';
import Donut from '../../../Charts/Donut';
import PanelCard from '../../../PanelCard';
import { AppTracker } from 'shared/analytics/tracker';
import { ANALYTIC_V3_EVENTS } from 'views/main/organization/analytics-v3/constants';
import Empty from 'views/main/organization/analytics-v3/icons/empty';
import Error from 'views/main/organization/analytics-v3/components/Error';
import { AxiosError } from 'axios';
const CountByStatusDonutLoader = () => {
  return (
    <svg
      width="497"
      height="150"
      viewBox="0 0 497 150"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M97.44 3C97.44 1.34314 98.7838 -0.00640243 100.439 0.0612964C114.499 0.636253 128.121 5.24373 139.668 13.3652C152.035 22.0631 161.418 34.3677 166.538 48.5999C171.658 62.8322 172.265 78.2988 168.277 92.8893C164.553 106.512 156.992 118.752 146.525 128.164C145.293 129.272 143.396 129.089 142.34 127.811L127.437 109.784C126.382 108.508 126.569 106.625 127.77 105.485C133.595 99.9535 137.813 92.9194 139.942 85.1318C142.335 76.3776 141.971 67.0976 138.899 58.5582C135.827 50.0189 130.197 42.6361 122.777 37.4174C116.177 32.7756 108.448 30.047 100.438 29.5004C98.7848 29.3876 97.44 28.0551 97.44 26.3983V3Z"
        fill="#ECEFF3"
      />
      <path
        d="M137.045 131.887C137.973 133.259 137.616 135.129 136.209 136.001C120.56 145.693 101.805 149.171 83.6623 145.672C65.5197 142.172 49.3999 131.967 38.472 117.147C37.4892 115.814 37.8531 113.946 39.2243 113.017L58.5845 99.9044C59.957 98.9748 61.8155 99.342 62.8353 100.649C69.3695 109.022 78.7324 114.781 89.2222 116.805C99.7117 118.828 110.543 116.964 119.718 111.622C121.151 110.788 123.013 111.138 123.941 112.511L137.045 131.887Z"
        fill="#ECEFF3"
      />
      <path
        d="M35.4963 107.056C34.0389 107.846 32.2113 107.306 31.4828 105.817C26.6023 95.8426 24.0383 84.8781 24.0004 73.746C23.9591 61.612 26.9205 49.6565 32.6202 38.9466C38.3199 28.2368 46.5807 19.1058 56.6651 12.3688C65.9166 6.18815 76.4389 2.19876 87.4329 0.685491C89.0745 0.459541 90.5415 1.67446 90.6999 3.3239L92.937 26.6148C93.0954 28.2639 91.8845 29.7188 90.25 29.9893C84.0835 31.0099 78.1924 33.334 72.975 36.8196C66.9244 40.8618 61.9679 46.3404 58.5481 52.7663C55.1283 59.1922 53.3515 66.3655 53.3762 73.6459C53.3976 79.9249 54.7585 86.115 57.3543 91.8063C58.0415 93.3131 57.5102 95.1291 56.0541 95.918L35.4963 107.056Z"
        fill="#ECEFF3"
      />
      <rect x="231" y="10" width="224" height="19" rx="3" fill="#ECEFF3" />
      <rect x="231" y="47" width="224" height="19" rx="3" fill="#ECEFF3" />
      <rect x="231" y="84" width="224" height="19" rx="3" fill="#ECEFF3" />
      <rect x="231" y="121" width="224" height="19" rx="3" fill="#ECEFF3" />
    </svg>
  );
};

export default function IncidentCountByStatus({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) {
  const {
    data,
    isLoading,
    isError,
    refetch: refetchCountByStatus,
    error,
  } = useGetAnalyticsCountByStatus({
    isOrgAnalytics,
  });

  const { isLoading: isLoadingCountByStatus, refetch: refetchCountByStatusExport } =
    useGetAnalyticsCountByStatusExport({ isOrgAnalytics });

  const handleExport = () => {
    AppTracker.track(
      ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_EXPORT_CLICKED,
      {
        'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': 'Incident Analytics',
        'Chart Title': 'Incident Count by Status',
      },
      true,
    );
    refetchCountByStatusExport();
  };

  const handleLegendSelect = (name: string) => {
    AppTracker.track(
      ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_LEVEL_FILTER_APPLIED,
      {
        'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': 'Incident Analytics',
        'Chart Title': 'Incident Count by Status',
        'Legend Name': name,
      },
      true,
    );
  };

  const isEmpty = data?.length === 0;

  return (
    <PanelCard title="Incident Count by Status" onExportClick={handleExport}>
      <Box position="relative" height={180}>
        {isLoading ? (
          <CountByStatusDonutLoader />
        ) : isEmpty ? (
          <Empty />
        ) : isError ? (
          <Error
            onRefreshClick={refetchCountByStatus}
            errorMessage={(error as AxiosError).response?.data?.message}
          />
        ) : (
          <Donut
            data={data ?? []}
            chartName="Incident Count by Status"
            onLegendSelect={handleLegendSelect}
          />
        )}
      </Box>
    </PanelCard>
  );
}
